import { useState } from 'react';
import { hubAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertOctagon, Send, Clock } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Affiche le blocage "plafond atteint" et permet de demander une autorisation.
 * Ouvre lorsqu'on essaye de créer un projet alors qu'on est au plafond.
 */
export function CreationCapBlockedDialog({ open, onOpenChange, limitInfo, onRequested }) {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const pending = limitInfo?.pending_request;

    const submit = async () => {
        if ((reason || '').trim().length < 10) {
            toast.error('Merci de préciser en 10 caractères minimum');
            return;
        }
        setSubmitting(true);
        try {
            await hubAPI.requestOverride(reason.trim());
            toast.success('Demande envoyée à un administrateur');
            onRequested?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="cap-blocked-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-700">
                        <AlertOctagon className="w-5 h-5" /> Plafond de projets actifs atteint
                    </DialogTitle>
                    <DialogDescription>
                        Tu es propriétaire de <strong>{limitInfo?.owned_active}</strong> projet(s) actif(s) sur un plafond de <strong>{limitInfo?.cap}</strong>.
                        Objectif : rester concentré et ne pas s&apos;éparpiller.
                    </DialogDescription>
                </DialogHeader>

                <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 space-y-1.5">
                    <div className="font-semibold">Deux options :</div>
                    <div>• <strong>Termine</strong> un projet en cours (marque-le comme Gagné ou Clôture-le) avant d&apos;en créer un nouveau.</div>
                    <div>• Ou <strong>demande une autorisation exceptionnelle</strong> à un administrateur (Jérémy Braille) ci-dessous.</div>
                </div>

                {pending ? (
                    <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900 flex items-start gap-2" data-testid="cap-pending">
                        <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                        <div>
                            <div className="font-semibold">Demande déjà envoyée</div>
                            <div className="text-xs mt-0.5">En attente de décision. Un email te sera envoyé dès qu&apos;elle sera traitée.</div>
                            <div className="text-[11px] text-blue-700 mt-1 italic">« {pending.reason} »</div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <Label>Raison de la demande (obligatoire)</Label>
                        <Textarea
                            rows={4}
                            placeholder="Explique pourquoi ce nouveau projet est prioritaire par rapport aux autres en cours. Ex : opportunité urgente, dossier client important, deadline courte…"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            data-testid="cap-reason"
                        />
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
                    {!pending && (
                        <Button onClick={submit} disabled={submitting} className="gap-1" data-testid="cap-submit">
                            <Send className="w-4 h-4" /> {submitting ? 'Envoi…' : 'Demander l\'autorisation'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
